import { IconButton } from "@material-ui/core";
import { Add, Close } from "@material-ui/icons";
import { createAccountType, deleteAccountType, updateAccountType } from "apis";
import { BackLink, Button, Intro, Loader, TextField } from "components/commons";
import { ConfirmModal } from "components/modals";
import { FormMode, Path } from "enums";
import AccountType from "enums/account-type";
import { useApi, useModal } from "hooks";
import locale from "localization";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { handleRequest } from "utils";
import { CountStepper, TierNumberInput } from "./account-type-vip-tier-inputs";
import AccountTypeVipTiersTable from "./account-type-vip-tiers-table";
import {
  FieldKind,
  formatByKind,
  isEmptyValue,
  TIER_FIELDS,
  toNumber,
  useAccountTypeGroupTiers,
} from "./account-type-vip-tiers.utils";
import styles from "./account-type-vip-tiers-form.module.scss";

let newTierCount = 0;

const toDraft = (tier) => ({
  key: tier.accountTypeId,
  accountTypeId: tier.accountTypeId,
  isNew: false,
  original: tier,
  touched: {},
  values: TIER_FIELDS.reduce(
    (values, [fieldKey, kind]) => ({ ...values, [fieldKey]: formatByKind(tier[fieldKey], kind) }),
    { name: tier.name || "" },
  ),
});

const isFieldInvalid = (draft, fieldKey) => {
  const REQUIRED_FIELDS = [
    "walletSize",
    "fuelTankSize",
    "totalCashinLimitDaily",
    "totalCashinLimitMonthly",
    "totalBuyFuelLimitDaily",
    "totalBuyFuelLimitMonthly",
    "totalRedeemLimitDaily",
    "totalRedemptionLiterLimitDaily",
    "totalPayWithPesoLimitDaily",
    "totalPayWithPesoLimitMonthly",
  ];
  const ZERO_ALLOWED_FIELDS = ["fuelTankSize", "tierThresholdLiters"];
  const value = draft.values[fieldKey];
  if (fieldKey === "name") {
    return !value.trim();
  }
  if (isEmptyValue(value)) {
    return (
      REQUIRED_FIELDS.includes(fieldKey) ||
      (!draft.isNew && !isEmptyValue(draft.original[fieldKey]))
    );
  }
  return ZERO_ALLOWED_FIELDS.includes(fieldKey) ? toNumber(value) < 0 : toNumber(value) <= 0;
};

const getChanges = (draft, group, tierLevel) => {
  const changes = {};
  if (group && tierLevel !== draft.original.tierLevel) {
    changes.tierLevel = tierLevel;
  }
  if (group && group.key !== draft.original.accountTypeGroup) {
    changes.accountTypeGroup = group.key;
  }
  if (group && group.name !== (draft.original.accountTypeGroupName || "")) {
    changes.accountTypeGroupName = group.name;
  }
  if (draft.values.name.trim() !== (draft.original.name || "")) {
    changes.name = draft.values.name.trim();
  }
  TIER_FIELDS.forEach(([fieldKey]) => {
    const value = toNumber(draft.values[fieldKey]);
    if (value !== toNumber(draft.original[fieldKey])) {
      changes[fieldKey] = value;
    }
  });
  return changes;
};

const AccountTypeVipTiersFormModule = ({ pageMode, accountTypeId }) => {
  const NAME_MAX_LENGTH = 75;
  const history = useHistory();
  const addMode = pageMode === FormMode.Add;
  const confirmModal = useModal();
  const { close } = confirmModal;

  const { loading, accountTypes, groupKey, groupLabel, tiers } =
    useAccountTypeGroupTiers(accountTypeId);
  const [drafts, setDrafts] = useState([]);
  const [removedTiers, setRemovedTiers] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [groupNameTouched, setGroupNameTouched] = useState(false);

  useEffect(() => {
    setDrafts(
      addMode
        ? [
            {
              key: `new-${(newTierCount += 1)}`,
              isNew: true,
              original: {},
              touched: {},
              values: TIER_FIELDS.reduce((values, [fieldKey]) => ({ ...values, [fieldKey]: "" }), {
                name: "",
              }),
            },
          ]
        : tiers.map(toDraft),
    );
    setRemovedTiers([]);
    setGroupName(groupKey ? groupLabel : "");
  }, [addMode, tiers, groupKey, groupLabel]);

  const trimmedGroupName = groupName.trim();
  const derivedGroupKey = trimmedGroupName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  const group = trimmedGroupName
    ? { key: groupKey || derivedGroupKey, name: trimmedGroupName }
    : null;
  const groupNameTaken =
    !!group &&
    accountTypes.some(
      (a) =>
        a.accountTypeGroup &&
        a.accountTypeGroup !== groupKey &&
        (a.accountTypeGroup === group.key ||
          (a.accountTypeGroupName || "").trim().toLowerCase() === group.name.toLowerCase()),
    );
  const groupNameInvalid =
    ((addMode || !!groupKey) && !group) || (!!group && !group.key) || groupNameTaken;

  const createRequest = useApi({ api: createAccountType, handleOwnError: { badrequest: true } });
  const updateRequest = useApi({ api: updateAccountType, handleOwnError: { badrequest: true } });
  const deleteRequest = useApi({ api: deleteAccountType, handleOwnError: { badrequest: true } });
  const saving = createRequest.loading || updateRequest.loading || deleteRequest.loading;

  const invalid =
    groupNameInvalid ||
    drafts.some((draft) =>
      ["name", ...TIER_FIELDS.map(([fieldKey]) => fieldKey)].some((fieldKey) =>
        isFieldInvalid(draft, fieldKey),
      ),
    );
  const hasChanges =
    removedTiers.length > 0 ||
    drafts.some(
      (draft, index) => draft.isNew || Object.keys(getChanges(draft, group, index + 1)).length > 0,
    );

  const updateDraft = (key, update) => {
    setDrafts((current) =>
      current.map((draft) => (draft.key === key ? { ...draft, ...update(draft) } : draft)),
    );
  };

  const changeField =
    (key) =>
    (fieldKey, { value }) => {
      updateDraft(key, (draft) => ({
        values: { ...draft.values, [fieldKey]: value },
        touched: { ...draft.touched, [fieldKey]: true },
      }));
    };

  const addTier = () => {
    setDrafts((current) => [
      ...current,
      {
        key: `new-${(newTierCount += 1)}`,
        isNew: true,
        original: {},
        touched: {},
        values: TIER_FIELDS.reduce((values, [fieldKey]) => ({ ...values, [fieldKey]: "" }), {
          name: "",
        }),
      },
    ]);
  };

  const removeTier = (draft) => {
    const removeDraft = () => {
      close();
      setDrafts((current) => current.filter(({ key }) => key !== draft.key));
      if (!draft.isNew) {
        setRemovedTiers((current) => [...current, draft]);
      }
    };
    if (draft.isNew) {
      removeDraft();
      return;
    }
    confirmModal.show({
      title: locale.deleteAccountTypeQuestion,
      content: (
        <locale.Populate
          text={locale.deleteAccountTypeContent}
          items={[<b>{draft.original.name}</b>]}
        />
      ),
      secondary: { text: locale.cancel },
      primary: { text: locale.yesDeleteIt, onClick: removeDraft },
    });
  };

  const save = () => {
    close();
    handleRequest(async () => {
      for (const draft of removedTiers) {
        await deleteRequest.request({ accountTypeId: draft.accountTypeId });
      }
      setRemovedTiers([]);
      const removedIds = removedTiers.map((draft) => draft.accountTypeId);
      let viewAccountTypeId = removedIds.includes(accountTypeId) ? null : accountTypeId;
      for (const [index, draft] of drafts.entries()) {
        const tierLevel = index + 1;
        if (draft.isNew) {
          const COPIED_FIELDS = [
            "totalTransactionCardDaily",
            "totalTransactionCardMonthly",
            "totalRedeemLimitType",
          ];
          const template =
            (addMode
              ? accountTypes.find((a) => a.isDefault) || accountTypes[0]
              : tiers[tiers.length - 1]) || {};
          const body = {
            name: draft.values.name.trim(),
            accountTypeGroup: group?.key,
            accountTypeGroupName: group?.name,
            tierLevel,
          };
          COPIED_FIELDS.forEach((fieldKey) => {
            body[fieldKey] =
              fieldKey === "totalRedeemLimitType"
                ? template[fieldKey]
                : toNumber(template[fieldKey]);
          });
          TIER_FIELDS.forEach(([fieldKey]) => {
            const value = toNumber(draft.values[fieldKey]);
            if (value !== null) {
              body[fieldKey] = value;
            }
          });
          const result = await createRequest.request(body);
          viewAccountTypeId = viewAccountTypeId || result.accountTypeId;
          updateDraft(draft.key, () => ({ ...toDraft(result), key: draft.key }));
        } else {
          viewAccountTypeId = viewAccountTypeId || draft.accountTypeId;
          const changes = getChanges(draft, group, tierLevel);
          if (Object.keys(changes).length > 0) {
            const result = await updateRequest.request({
              accountTypeId: draft.accountTypeId,
              ...changes,
            });
            updateDraft(draft.key, () => ({ ...toDraft(result), key: draft.key }));
          }
        }
      }
      history.push(Path.ViewAccountType, { accountTypeId: viewAccountTypeId });
    });
  };

  const handleSave = () => {
    confirmModal.show({
      title: addMode ? locale.createAccountTypeQuestion : locale.saveChangesQuestion,
      content: addMode ? locale.areYouSureCreatingAccountType : locale.areYouSureSavingAccountType,
      secondary: { text: addMode ? locale.cancel : locale.continueEditing },
      primary: { text: addMode ? locale.yesCreateAccountType : locale.saveChanges, onClick: save },
    });
  };

  const renderHeader = (draft) => (
    <div className={styles.tierHeader}>
      <TextField
        className={styles.tierName}
        name="name"
        value={draft.values.name}
        placeholder={locale.tierName}
        maxLength={NAME_MAX_LENGTH}
        fontColor="var(--color-night)"
        error={isFieldInvalid(draft, "name") && (!draft.isNew || draft.touched.name)}
        disabled={saving}
        onChange={changeField(draft.key)}
      />
      {drafts.length > 1 &&
        !draft.original.isDefault &&
        draft.original.type !== AccountType.TNVS && (
          <IconButton
            className={styles.removeTier}
            size="small"
            disabled={saving}
            onClick={() => removeTier(draft)}
          >
            <Close fontSize="inherit" />
          </IconButton>
        )}
    </div>
  );

  const renderField = (draft, fieldKey, kind) => {
    const inputProps = {
      name: fieldKey,
      value: draft.values[fieldKey],
      error: isFieldInvalid(draft, fieldKey) && (!draft.isNew || draft.touched[fieldKey]),
      disabled: saving,
      onChange: changeField(draft.key),
    };
    return kind === FieldKind.Count ? (
      <CountStepper {...inputProps} />
    ) : (
      <TierNumberInput {...inputProps} kind={kind} />
    );
  };

  return (
    <div>
      <ConfirmModal
        {...confirmModal}
        titleStyle={styles.modalTitle}
        contentStyle={styles.modalContent}
      />
      <BackLink text={locale.accountTypes} path={Path.AccountTypes} />
      <div className={styles.intro}>
        <Intro
          actionText={addMode ? locale.create : locale.save}
          actionOnClick={handleSave}
          actionClassName={styles.primarySaveButton}
          actionLoading={saving}
          actionDisabled={loading || invalid || !hasChanges}
          actionSecondaryText={locale.cancel}
          actionSecondaryOutlined
          actionSecondaryOnClick={() => {
            history.goBack();
          }}
          actionSecondaryClassName={styles.secondarySaveButton}
          title={addMode ? locale.createAccountType : locale.editAccountTypeTitle}
          titleStyle={styles.title}
        />
      </div>

      <Loader open={loading} />
      {!loading && drafts.length > 0 && (
        <AccountTypeVipTiersTable
          groupLabel={
            <TextField
              className={styles.groupName}
              name="accountTypeGroupName"
              value={groupName}
              placeholder={locale.accountTypeName}
              maxLength={NAME_MAX_LENGTH}
              fontColor="var(--color-night)"
              error={groupNameInvalid && (groupNameTouched || groupNameTaken || !addMode)}
              helperText={groupNameTaken ? locale.accountTypeNameAlreadyExists : ""}
              disabled={saving}
              onChange={(_, { value }) => {
                setGroupName(value);
                setGroupNameTouched(true);
              }}
            />
          }
          columns={drafts}
          renderHeader={renderHeader}
          renderField={renderField}
          headerAction={
            (addMode || group) && (
              <Button
                outline
                className={styles.addTier}
                startIcon={<Add />}
                disabled={saving}
                onClick={addTier}
              >
                {locale.addTier}
              </Button>
            )
          }
        />
      )}
    </div>
  );
};

export default AccountTypeVipTiersFormModule;
